import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { IProviderDTO, IProviderPaymentCalendarViewConfig, ProviderPaymentDateRuleDTO } from '../../types';
import { findPaymentDateRule, getProviderPaymentCalendarViewConfig } from '../../services';
import { ProviderEditDrawer } from '../ProviderEditDrawer';
import ProviderPaymentDateRuleModal from '../ProviderPaymentDateRuleModal';
import ProviderPaymentCalendarViewConfigModal from '../ProviderPaymentCalendarViewConfigModal';
import { buildPaymentRuleSummary } from '../../helpers/paymentRuleSummary';
import { buildProviderDetailUrl, ProviderDetailTab, providerDetailTabFromSearch } from '../../helpers/detailView';
import { ProviderPayablesList } from 'features/salesOrder/components/ProviderPayablesList';
import { ProviderPayablesPaymentsList } from 'features/salesOrder/components/ProviderPayablesPaymentsList';
import { ProviderPayablesScheduleView } from 'features/salesOrder/components/ProviderPayablesScheduleView';

interface ProviderDetailProps {
	provider: IProviderDTO;
	onProviderUpdated: () => void;
}

const DETAIL_TABS: { value: ProviderDetailTab; label: string }[] = [
	{ value: 'data', label: 'Datos' },
	{ value: 'payment-rule', label: 'Regla de pago' },
	{ value: 'calendar-config', label: 'Calendario' },
	{ value: 'schedule', label: 'Agenda de pagos' },
	{ value: 'payables', label: 'Órdenes de compra' },
	{ value: 'payments', label: 'Pagos realizados' },
];

const formatOptional = (value?: string | number | null): string => {
	if (value === undefined || value === null || value === '') return '—';
	return String(value);
};

interface ProviderDetailTabPanelProps {
	value: ProviderDetailTab;
	activeTab: ProviderDetailTab;
	children: React.ReactNode;
}

const ProviderDetailTabPanel = ({ value, activeTab, children }: ProviderDetailTabPanelProps): JSX.Element => (
	<Box role="tabpanel" hidden={activeTab !== value} sx={activeTab === value ? undefined : { display: 'none' }}>
		{children}
	</Box>
);

const ProviderDetail = ({ provider, onProviderUpdated }: ProviderDetailProps): JSX.Element => {
	const location = useLocation();
	const [activeTab, setActiveTab] = useState<ProviderDetailTab>(() => providerDetailTabFromSearch(location.search));
	const [visitedTabs, setVisitedTabs] = useState<ProviderDetailTab[]>(() => [
		providerDetailTabFromSearch(location.search),
	]);

	const [paymentRule, setPaymentRule] = useState<ProviderPaymentDateRuleDTO | null>(null);
	const [calendarConfig, setCalendarConfig] = useState<IProviderPaymentCalendarViewConfig | null>(null);
	const [ruleLoading, setRuleLoading] = useState(false);
	const [calendarLoading, setCalendarLoading] = useState(false);
	const [ruleError, setRuleError] = useState<string | null>(null);
	const [calendarError, setCalendarError] = useState<string | null>(null);
	const [ruleLoaded, setRuleLoaded] = useState(false);
	const [calendarLoaded, setCalendarLoaded] = useState(false);

	const [editDrawerOpen, setEditDrawerOpen] = useState(false);
	const [openRuleModal, setOpenRuleModal] = useState(false);
	const [openCalendarModal, setOpenCalendarModal] = useState(false);

	const loadPaymentRule = useCallback(async () => {
		setRuleLoading(true);
		setRuleError(null);
		try {
			const resp = await findPaymentDateRule(provider.id);
			setPaymentRule(resp.status === 404 ? null : resp.data);
		} catch {
			setPaymentRule(null);
			setRuleError('No se pudo cargar la regla de fecha de pago.');
		} finally {
			setRuleLoading(false);
			setRuleLoaded(true);
		}
	}, [provider.id]);

	const loadCalendarConfig = useCallback(async () => {
		setCalendarLoading(true);
		setCalendarError(null);
		try {
			const resp = await getProviderPaymentCalendarViewConfig(provider.id);
			setCalendarConfig(resp.data);
		} catch {
			setCalendarConfig(null);
			setCalendarError('No se pudo cargar la configuración del calendario.');
		} finally {
			setCalendarLoading(false);
			setCalendarLoaded(true);
		}
	}, [provider.id]);

	useEffect(() => {
		const tabFromUrl = providerDetailTabFromSearch(location.search);
		setActiveTab(tabFromUrl);
		setVisitedTabs((prev) => (prev.includes(tabFromUrl) ? prev : [...prev, tabFromUrl]));
	}, [location.search]);

	useEffect(() => {
		setRuleLoaded(false);
		setCalendarLoaded(false);
		setPaymentRule(null);
		setCalendarConfig(null);
		setRuleError(null);
		setCalendarError(null);
	}, [provider.id]);

	useEffect(() => {
		if (activeTab === 'payment-rule' && !ruleLoaded) loadPaymentRule();
	}, [activeTab, ruleLoaded, loadPaymentRule]);

	useEffect(() => {
		if (activeTab === 'calendar-config' && !calendarLoaded) loadCalendarConfig();
	}, [activeTab, calendarLoaded, loadCalendarConfig]);

	const handleTabChange = (_event: React.SyntheticEvent, value: ProviderDetailTab) => {
		setActiveTab(value);
		setVisitedTabs((prev) => (prev.includes(value) ? prev : [...prev, value]));
		window.history.replaceState(window.history.state, '', buildProviderDetailUrl(provider.id, value));
	};

	const dataFields = [
		{ label: 'ID', value: provider.id },
		{ label: 'Nombre fantasía', value: provider.fantasyName },
		{ label: 'Razón social', value: provider.businessName },
		{ label: 'RUT', value: formatOptional(provider.rut) },
		{ label: 'País', value: provider.country?.name },
		{ label: 'Estado / Provincia', value: provider.stateName },
		{ label: 'Ciudad', value: provider.cityName },
		{ label: 'Dirección', value: formatOptional(provider.street) },
		{ label: 'Código postal', value: formatOptional(provider.postalCode) },
		{ label: 'Teléfono', value: formatOptional(provider.phone) },
		{ label: 'Email', value: formatOptional(provider.email) },
	];

	const ruleSummary = buildPaymentRuleSummary(paymentRule);

	return (
		<Box>
			<Box
				sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
			>
				<Box>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{provider.fantasyName}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{provider.businessName}
					</Typography>
				</Box>
				<Chip
					label={provider.enable ? 'Activo' : 'Inactivo'}
					color={provider.enable ? 'success' : 'default'}
					variant="outlined"
				/>
			</Box>

			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				variant="scrollable"
				allowScrollButtonsMobile
				sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
			>
				{DETAIL_TABS.map((tab) => (
					<Tab key={tab.value} label={tab.label} value={tab.value} />
				))}
			</Tabs>

			<ProviderDetailTabPanel value="data" activeTab={activeTab}>
				<Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
						<Button variant="outlined" onClick={() => setEditDrawerOpen(true)}>
							Editar proveedor
						</Button>
					</Box>
					<Grid container spacing={2}>
						{dataFields.map((field) => (
							<Grid item xs={12} sm={6} md={4} key={field.label}>
								<Typography variant="caption" color="text.secondary" display="block">
									{field.label}
								</Typography>
								<Typography variant="body1">{field.value}</Typography>
							</Grid>
						))}
					</Grid>
				</Paper>
			</ProviderDetailTabPanel>

			{visitedTabs.includes('payment-rule') && (
				<ProviderDetailTabPanel value="payment-rule" activeTab={activeTab}>
					<Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
							<Button variant="outlined" onClick={() => setOpenRuleModal(true)}>
								{paymentRule ? 'Editar regla' : 'Configurar regla'}
							</Button>
						</Box>
						{ruleLoading ? (
							<Box display="flex" justifyContent="center" py={4}>
								<CircularProgress />
							</Box>
						) : (
							<>
								{ruleError && (
									<Alert severity="error" sx={{ mb: 2 }}>
										{ruleError}
									</Alert>
								)}
								<Grid container spacing={2}>
									{ruleSummary.map((field) => (
										<Grid item xs={12} sm={6} md={4} key={field.label}>
											<Typography variant="caption" color="text.secondary" display="block">
												{field.label}
											</Typography>
											<Typography variant="body1">{field.value}</Typography>
										</Grid>
									))}
								</Grid>
							</>
						)}
					</Paper>
				</ProviderDetailTabPanel>
			)}

			{visitedTabs.includes('calendar-config') && (
				<ProviderDetailTabPanel value="calendar-config" activeTab={activeTab}>
					<Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
							<Button variant="outlined" onClick={() => setOpenCalendarModal(true)}>
								Editar configuración
							</Button>
						</Box>
						{calendarLoading ? (
							<Box display="flex" justifyContent="center" py={4}>
								<CircularProgress />
							</Box>
						) : (
							<>
								{calendarError && (
									<Alert severity="error" sx={{ mb: 2 }}>
										{calendarError}
									</Alert>
								)}
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Typography variant="caption" color="text.secondary" display="block">
											Agrupado en calendario
										</Typography>
										<Typography variant="body1">{calendarConfig?.isGrouped ? 'Sí' : 'No (fila individual)'}</Typography>
									</Grid>
									{calendarConfig?.isGrouped && (
										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary" display="block">
												Grupo
											</Typography>
											<Typography variant="body1">
												{calendarConfig.groupIdName ?? `Grupo #${calendarConfig.groupId}`}
											</Typography>
										</Grid>
									)}
								</Grid>
							</>
						)}
					</Paper>
				</ProviderDetailTabPanel>
			)}

			{visitedTabs.includes('schedule') && (
				<ProviderDetailTabPanel value="schedule" activeTab={activeTab}>
					<ProviderPayablesScheduleView fixedProviderId={provider.id} />
				</ProviderDetailTabPanel>
			)}

			{visitedTabs.includes('payables') && (
				<ProviderDetailTabPanel value="payables" activeTab={activeTab}>
					<ProviderPayablesList fixedProviderId={provider.id} />
				</ProviderDetailTabPanel>
			)}

			{visitedTabs.includes('payments') && (
				<ProviderDetailTabPanel value="payments" activeTab={activeTab}>
					<ProviderPayablesPaymentsList fixedProviderId={provider.id} />
				</ProviderDetailTabPanel>
			)}

			<ProviderEditDrawer
				open={editDrawerOpen}
				providerId={provider.id}
				onClose={() => setEditDrawerOpen(false)}
				onSaved={onProviderUpdated}
			/>

			<ProviderPaymentDateRuleModal
				open={openRuleModal}
				providerId={provider.id}
				providerFantasyName={provider.fantasyName}
				onClose={() => setOpenRuleModal(false)}
				onSaved={() => {
					loadPaymentRule();
					onProviderUpdated();
				}}
			/>

			<ProviderPaymentCalendarViewConfigModal
				open={openCalendarModal}
				providerId={provider.id}
				providerFantasyName={provider.fantasyName}
				onClose={() => setOpenCalendarModal(false)}
				onSaved={loadCalendarConfig}
			/>
		</Box>
	);
};

export default ProviderDetail;
